// How references are written in markdown: `[[kind:id|name]]` (the name may be
// empty) and `[[name]]` (a title link, no id). Pure and dependency-free so the
// server, the screen and the editor read references the same way.
//
// Promise:
// - Any snake_case kind is a reference. Whether a part knows the kind is not
//   this module's business: an unknown kind is read, kept and written back as
//   it was (a document written by a newer or bigger trameria keeps its links).
// - Code is never read or rewritten: fenced code, indented code and inline code
//   spans. `codeRanges` is the one rule, and reading and replacing both skip
//   exactly its ranges.
// - A reference never spans a line break, and its name never contains `]`.
// - `formatRef` drops what would end a reference early (`]`, line breaks), so a
//   name cannot smuggle in a second reference.

/** A kind is lower snake_case. */
export const REF_KIND_PATTERN = "[a-z][a-z0-9_]*";

/** Marks a reference that could not be resolved, in answers to AI (MCP). Removed when saved. */
// i18n-ignore: stored with the text (a name suffix that saving strips again)
export const REF_NOT_FOUND = "(見つからない)";

/**
 * The most different references (and URLs) one text is read for when saving:
 * named, resolved and recorded as links. Past it the text is kept as written
 * and the rest are not links (each one costs lookups and a row).
 */
export const MAX_REFS_PER_TEXT = 5000;

/** A readable key (pages, chats): 12 lowercase hex characters. */
export const KEY_PATTERN = /^[0-9a-f]{12}$/;
export const isKey = (s: string): boolean => KEY_PATTERN.test(s);

export interface RefToken {
  /** null for a title link (`[[name]]`). */
  kind: string | null;
  id: string | null;
  /** The name as written (trimmed for title links). */
  label: string;
  /** The reference exactly as written. */
  raw: string;
  /** Where `raw` starts in the markdown. */
  at: number;
}

// group 1 = kind, 2 = id, 3 = name (id form); group 4 = title (title form).
const REF_SOURCE = `\\[\\[(?:(${REF_KIND_PATTERN}):([^|\\]\\n]+)\\|([^\\]\\n]*)|([^\\]|\\n]+))\\]\\]`;
const REF_RE = new RegExp(REF_SOURCE, "g");
const REF_AT_START_RE = new RegExp(`^(?:${REF_SOURCE})`);

function toToken(m: RegExpExecArray | RegExpMatchArray, at: number): RefToken {
  if (m[1]) return { kind: m[1], id: m[2], label: m[3] ?? "", raw: m[0], at };
  return { kind: null, id: null, label: (m[4] ?? "").trim(), raw: m[0], at };
}

/** The reference starting at the beginning of `src`, if any (for tokenizers). */
export function matchRefAtStart(src: string): RefToken | undefined {
  const m = REF_AT_START_RE.exec(src);
  return m ? toToken(m, 0) : undefined;
}

// ---- code ------------------------------------------------------------------

type Range = [start: number, end: number];

/** Strips blockquote and list markers, one level at a time. */
function stripContainers(line: string): { rest: string; inList: boolean } {
  let s = line;
  let inList = false;
  for (;;) {
    const m = /^[ \t]{0,3}(?:>[ \t]?|([-*+]|\d{1,9}[.)])[ \t]+)/.exec(s);
    if (!m) break;
    if (m[1]) inList = true;
    s = s.slice(m[0].length);
  }
  return { rest: s, inList };
}

const indentWidth = (s: string): number => {
  let w = 0;
  for (const ch of s) {
    if (ch === " ") w += 1;
    else if (ch === "\t") w += 4 - (w % 4);
    else break;
  }
  return w;
};

/**
 * Inline code spans in one line, as CommonMark reads them: a run of backticks
 * opens, the next run of the same length closes, and a run with no such run
 * after it is plain text. Linear in the line: each run's next run of the same
 * length is found once (a pattern that tried every run against the rest of
 * the line took tens of seconds on a long line of runs of shrinking length).
 */
function codeSpans(line: string): Range[] {
  const runs: Range[] = [];
  for (const m of line.matchAll(/`+/g)) {
    const at = m.index ?? 0;
    runs.push([at, at + m[0].length]);
  }
  if (runs.length < 2) return [];
  const nextSame = new Array<number>(runs.length).fill(-1);
  const lastOf = new Map<number, number>();
  for (let i = runs.length - 1; i >= 0; i--) {
    const length = runs[i][1] - runs[i][0];
    nextSame[i] = lastOf.get(length) ?? -1;
    lastOf.set(length, i);
  }
  const out: Range[] = [];
  for (let i = 0; i < runs.length; i++) {
    const j = nextSame[i];
    if (j === -1) continue;
    out.push([runs[i][0], runs[j][1]]);
    i = j;
  }
  return out;
}

/**
 * The ranges of the markdown that are code, [start, end). Errs toward code:
 * an unclosed fence runs to the end, and an indented line after a blank line
 * (outside lists) is code.
 */
export function codeRanges(markdown: string): Range[] {
  const ranges: Range[] = [];
  const lines = markdown.split("\n");
  let offset = 0;
  let fence: { char: string; length: number; start: number } | null = null;
  let indented: { start: number; end: number } | null = null;
  let previousBlank = true;

  const closeIndented = () => {
    if (indented) ranges.push([indented.start, indented.end]);
    indented = null;
  };

  for (const line of lines) {
    const start = offset;
    const end = offset + line.length;
    offset = end + 1;
    const { rest, inList } = stripContainers(line);
    const blank = rest.trim() === "";

    if (fence) {
      const t = rest.trim();
      if (t.length >= fence.length && [...t].every((c) => c === fence?.char)) {
        ranges.push([fence.start, end]);
        fence = null;
      }
      previousBlank = false;
      continue;
    }

    const open = /^[ \t]{0,3}(`{3,}|~{3,})/.exec(rest);
    if (open) {
      closeIndented();
      fence = { char: open[1][0], length: open[1].length, start };
      previousBlank = false;
      continue;
    }

    if (!inList && indentWidth(line) >= 4 && (indented || previousBlank)) {
      if (!blank) {
        if (indented) indented.end = end;
        else indented = { start, end };
      }
      previousBlank = blank;
      continue;
    }
    if (!blank) closeIndented();

    const lead = line.length - rest.length;
    for (const [s, e] of codeSpans(rest))
      ranges.push([start + lead + s, start + lead + e]);
    previousBlank = blank;
  }
  if (fence) ranges.push([fence.start, markdown.length]);
  closeIndented();
  return ranges.sort((a, b) => a[0] - b[0]);
}

/**
 * Whether [start, end) touches code. The ranges are merged once and looked up
 * by binary search, so reading a body is not "references × code spans" (a
 * body of a million characters alternating the two took tens of seconds).
 */
function codeOverlap(
  markdown: string,
): (start: number, end: number) => boolean {
  const merged: Range[] = [];
  for (const [s, e] of codeRanges(markdown)) {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  }
  return (start, end) => {
    // The first range ending after `start` (ends grow with starts once merged).
    let lo = 0;
    let hi = merged.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (merged[mid][1] <= start) lo = mid + 1;
      else hi = mid;
    }
    return lo < merged.length && merged[lo][0] < end;
  };
}

/** Where the line holding `pos` starts, by binary search over the line starts. */
function lineStarts(markdown: string): (pos: number) => number {
  const starts = [0];
  for (
    let i = markdown.indexOf("\n");
    i !== -1;
    i = markdown.indexOf("\n", i + 1)
  )
    starts.push(i + 1);
  return (pos) => {
    let lo = 0;
    let hi = starts.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >>> 1;
      if (starts[mid] <= pos) lo = mid;
      else hi = mid - 1;
    }
    return starts[lo];
  };
}

// ---- reading -----------------------------------------------------------------

/** The references outside code, in the order they appear. */
export function parseRefs(markdown: string): RefToken[] {
  if (!markdown.includes("[[")) return [];
  const inCode = codeOverlap(markdown);
  const out: RefToken[] = [];
  for (const m of markdown.matchAll(REF_RE)) {
    const at = m.index ?? 0;
    if (inCode(at, at + m[0].length)) continue;
    out.push(toToken(m, at));
  }
  return out;
}

/** The references reduced to their names, for one-line plain text. */
export function plainRefText(markdown: string): string {
  return markdown.replace(
    REF_RE,
    (_m, _kind, _id, label: string | undefined, title: string | undefined) =>
      typeof label === "string" ? label : (title ?? "").trim(),
  );
}

// ---- writing -----------------------------------------------------------------

function sanitizeLabel(label: string): string {
  return label
    .replace(/[\]\r\n]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/** `[[kind:id|name]]`. */
export function formatRef(ref: {
  kind: string;
  id: string;
  label: string;
}): string {
  return `[[${ref.kind}:${ref.id}|${sanitizeLabel(ref.label)}]]`;
}

/** `[[name]]` (a title link). `|` is dropped too, or it would read as an id form. */
export function formatTitleRef(label: string): string {
  return `[[${sanitizeLabel(label).replace(/\|/g, " ").trim()}]]`;
}

/**
 * Replaces references written as `raw` (keys of `replacements`) outside code.
 * Works on the text by position: parsing and serializing again would escape
 * `[` and rewrite bare URLs.
 */
export function replaceRefs(
  markdown: string,
  replacements: ReadonlyMap<string, string>,
): string {
  if (replacements.size === 0) return markdown;
  const inCode = codeOverlap(markdown);
  let out = "";
  let last = 0;
  for (const m of markdown.matchAll(REF_RE)) {
    const at = m.index ?? 0;
    const next = replacements.get(m[0]);
    if (next === undefined || inCode(at, at + m[0].length)) continue;
    out += markdown.slice(last, at) + next;
    last = at + m[0].length;
  }
  return last === 0 ? markdown : out + markdown.slice(last);
}

// ---- bare URLs ---------------------------------------------------------------

// Characters GFM drops from the end of a bare URL.
const URL_TRAILING = "?!.,:*_~)";
const isUrlChar = (ch: string | undefined) => {
  if (ch === undefined) return false;
  const code = ch.charCodeAt(0);
  // A bare URL ends before a non-ASCII character.
  return code > 0x20 && code <= 0x7e && ch !== "<";
};
// Before one of these, a URL is part of another word (`href="…"`, `x/https://…`).
const URL_LEFT_BLOCK = /[A-Za-z0-9\-./:=?&#%@+~$"'\\<]/;

function continuesUrl(markdown: string, end: number): boolean {
  let j = end;
  while (j < markdown.length && URL_TRAILING.includes(markdown[j])) j++;
  const ch = markdown[j];
  return isUrlChar(ch) && !URL_TRAILING.includes(ch);
}

/**
 * The questions asked about the line around a position. Asked at growing
 * positions (as both callers walk the text), each answer continues from the
 * last, so a long line is read once rather than once per position.
 */
function lineFacts(markdown: string) {
  const lineStartOf = lineStarts(markdown);
  // insideBracket's walk: depth of `[` over [line, at).
  let line = -1;
  let at = 0;
  let depth = 0;
  // afterLinkDefinition's line: where `[label]:` and the spaces after it end.
  let defLine = -1;
  let def: { from: number; to: number } | null = null;
  return {
    /** An unclosed `[` earlier on the line: inside link text or a reference. */
    insideBracket(pos: number): boolean {
      const start = lineStartOf(pos);
      if (start !== line || pos < at) {
        line = start;
        at = start;
        depth = 0;
      }
      for (; at < pos; at++) {
        if (markdown[at] === "[") depth++;
        else if (markdown[at] === "]") depth = Math.max(0, depth - 1);
      }
      return depth > 0;
    },
    /** Right after `[label]:` (and spaces) at the start of the line. */
    afterLinkDefinition(pos: number): boolean {
      const start = lineStartOf(pos);
      if (start !== defLine) {
        defLine = start;
        def = null;
        const open = /^ {0,3}\[/.exec(markdown.slice(start, start + 4));
        if (open) {
          const close = markdown.indexOf("]", start + open[0].length);
          const end = markdown.indexOf("\n", start);
          const lineEnd = end === -1 ? markdown.length : end;
          if (close !== -1 && close < lineEnd && markdown[close + 1] === ":") {
            let to = close + 2;
            while (to < lineEnd && /\s/.test(markdown[to])) to++;
            def = { from: close + 2, to };
          }
        }
      }
      return def !== null && pos >= def.from && pos <= def.to;
    },
  };
}

const count = (s: string, ch: string) => s.split(ch).length - 1;

/**
 * A bare URL as the editor's autolink reads it (GFM's, stopped before the
 * first non-ASCII character): Japanese is written with no space after a URL,
 * so a URL followed by a full-width full stop and more text, or wrapped in
 * full-width parentheses, must end before that mark. Then GFM's end:
 * `?!.,:*_~` drop, and `)` drops while there are more `)` than `(`.
 */
function bareUrlEnd(raw: string): string {
  let s = raw;
  for (let i = 0; i < s.length; i++)
    if (!isUrlChar(s[i])) {
      s = s.slice(0, i);
      break;
    }
  // Counted once (not again for each `)` dropped from a long run of them).
  let close = count(s, ")");
  const open = count(s, "(");
  let end = s.length;
  for (;;) {
    const last = s[end - 1];
    if (last !== undefined && "?!.,:*_~".includes(last)) end--;
    else if (last === ")" && close > open) {
      end--;
      close--;
    } else return s.slice(0, end);
  }
}

export interface BareUrl {
  /** The URL as written (also the key for `replaceBareUrls`). */
  url: string;
  at: number;
}

/**
 * URLs written on their own outside code: a bare URL, `<URL>`, or `[URL](URL)`
 * (what the editor writes for a bare URL). A link with chosen words
 * (`[words](URL)`) is the writer's choice and is not one. A bare URL ends as
 * the editor's autolink ends it (`bareUrlEnd`); `<URL>` and `[URL](URL)` take
 * what is written between their marks (a URL with Japanese in it is written
 * so).
 */
export function findBareUrls(markdown: string): BareUrl[] {
  if (!markdown.includes("://")) return [];
  const inCode = codeOverlap(markdown);
  const line = lineFacts(markdown);
  const out: BareUrl[] = [];
  const re = /https?:\/\/[^\s<>[\]]+|trameria:\/\/[^\s<>[\]]+/g;
  for (let m = re.exec(markdown); m; m = re.exec(markdown)) {
    const at = m.index;
    const raw = m[0];
    // Between the marks the whole is the URL (the match stops at `>` / `]`).
    const marked =
      (markdown[at - 1] === "<" && markdown[at + raw.length] === ">") ||
      markdown.startsWith(`[${raw}](${raw})`, at - 1);
    const url = marked ? raw : bareUrlEnd(raw);
    // The next URL may start right after this one's end (two URLs in
    // full-width brackets with only a Japanese particle between them).
    re.lastIndex = at + Math.max(url.length, 1);
    if (!/:\/\/./.test(url)) continue;
    const self = markdown.startsWith(`[${url}](${url})`, at - 1);
    const angled =
      markdown[at - 1] === "<" && markdown[at + url.length] === ">";
    if (!self && !angled) {
      const prev = markdown[at - 1];
      if (prev !== undefined && URL_LEFT_BLOCK.test(prev)) continue;
      if (markdown.slice(Math.max(0, at - 2), at).endsWith("](")) continue;
      if (line.insideBracket(at) || line.afterLinkDefinition(at)) continue;
    }
    if (inCode(at, at + url.length)) continue;
    out.push({ url, at });
  }
  return out;
}

/**
 * Replaces URLs written on their own (see `findBareUrls`): the bare URL, the
 * angle brackets with it, or the whole `[URL](URL)`.
 */
export function replaceBareUrls(
  markdown: string,
  replacements: ReadonlyMap<string, string>,
): string {
  if (replacements.size === 0) return markdown;
  const inCode = codeOverlap(markdown);
  const line = lineFacts(markdown);
  const urls = [...replacements.keys()].filter((u) => u.length > 0);
  const heads = new Set(["[", "<", ...urls.map((u) => u[0])]);
  // The URLs in a trie: the ones written at a position are found in one walk,
  // not by trying every URL at every position.
  type Node = { next: Map<string, Node>; url?: string };
  const root: Node = { next: new Map() };
  for (const url of urls) {
    let node = root;
    for (const ch of url) {
      let child = node.next.get(ch);
      if (!child) {
        child = { next: new Map() };
        node.next.set(ch, child);
      }
      node = child;
    }
    node.url = url;
  }
  /** The URLs written from `from`, longest first (`…/abc/name` before `…/abc`). */
  const urlsAt = (from: number): string[] => {
    const found: string[] = [];
    let node: Node | undefined = root;
    for (let j = from; node && j <= markdown.length; j++) {
      if (node.url !== undefined) found.push(node.url);
      if (j === markdown.length) break;
      node = node.next.get(markdown[j]);
    }
    return found.reverse();
  };
  const matchAt = (i: number): { end: number; to: string } | null => {
    const ch = markdown[i];
    if (!heads.has(ch)) return null;
    const before = markdown.slice(Math.max(0, i - 2), i);
    if (before.endsWith("](") || before.endsWith("!")) return null;
    if (line.insideBracket(i) || line.afterLinkDefinition(i)) return null;
    const marked = ch === "[" || ch === "<";
    for (const url of urlsAt(marked ? i + 1 : i)) {
      const to = replacements.get(url) as string;
      if (ch === "[") {
        const self = `[${url}](${url})`;
        if (markdown.startsWith(self, i)) return { end: i + self.length, to };
        continue;
      }
      if (ch === "<") {
        if (markdown.startsWith(`<${url}>`, i))
          return { end: i + url.length + 2, to };
        continue;
      }
      const prev = markdown[i - 1];
      if (prev !== undefined && URL_LEFT_BLOCK.test(prev)) return null;
      if (continuesUrl(markdown, i + url.length)) continue;
      return { end: i + url.length, to };
    }
    return null;
  };
  let out = "";
  let i = 0;
  let last = 0;
  while (i < markdown.length) {
    const m = matchAt(i);
    if (m && !inCode(i, m.end)) {
      out += markdown.slice(last, i) + m.to;
      i = m.end;
      last = i;
      continue;
    }
    i += 1;
  }
  return last === 0 ? markdown : out + markdown.slice(last);
}

// ---- paths of URLs ------------------------------------------------------------
// A ref kind's `paths` (`/me/cases/:id`, `/:slug/documents/:key/*`): `:id` /
// `:key` name it (the last one wins), another `:name` takes any one segment, a
// trailing `/*` lets any decoration follow. The server (a written URL) and the
// screen (a pasted one) read them the same way.

/** What `pattern` takes from a URL path's segments (decoded), or null. */
export function matchRefPath(
  pattern: string,
  seg: readonly string[],
): { id: string } | { key: string } | null {
  const parts = pattern.split("/").filter(Boolean);
  const open = parts[parts.length - 1] === "*";
  const fixed = open ? parts.slice(0, -1) : parts;
  if (open ? seg.length < fixed.length : seg.length !== fixed.length)
    return null;
  let named: { id: string } | { key: string } | null = null;
  for (let i = 0; i < fixed.length; i++) {
    const p = fixed[i];
    if (p === ":id") named = { id: seg[i] };
    else if (p === ":key") named = { key: seg[i] };
    else if (!p.startsWith(":") && p !== seg[i]) return null;
  }
  return named;
}

/**
 * Where a pattern is tried among all kinds' (the lower, the earlier): the more
 * literal segments first, so `/me/documents/index/:key` comes before
 * `/me/documents/:key/*` takes "index" for a key; of as many, one without a
 * trailing `/*` first.
 */
export function refPathOrder(pattern: string): number {
  const parts = pattern.split("/").filter(Boolean);
  const literal = parts.filter((s) => !s.startsWith(":") && s !== "*").length;
  return -literal * 2 + (parts.at(-1) === "*" ? 1 : 0);
}
